import {
  CartUtils,
  Discount,
  Guid,
  MobileProductDisplayAttributes,
  NbnProductDisplayAttributes,
  RetrieveCartItem,
  RetrieveCartResponse,
  SessionStorageKeys
} from '@aglenergy/shared-library';
import { BundleConfig } from 'app/shared/models/bundle-config.model';
import { BundleConfigOptions } from 'app/shared/models/bundle-config-options.model';
import { BundleItem, BundleTileItems } from 'app/shared/models/bundle/displayCartItem.model';
import { CustomerProfile } from 'app/shared/models/customerProfile';
import { CustomerType } from 'app/shared/models/productSearchRequestModel';
import { DataSpaKey } from 'app/shared/models/dataSpa';
import { Frequency } from 'app/shared/models/contracts-api.model';
import { ProductCatalogueProduct } from 'app/shared/models/product-catalogue-product.model';
import { ProductCategory } from 'app/shared/models/product-category.model';
import { QueryStringParamName } from 'app/shared/models/common.enums';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';
import { getDataSpaSearchParams } from 'app/shared/utilities/helper-functions';

class BundleService {
  public isExplicitBundleFlow(): boolean {
    const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
    const isNbnMoversBundleFlow =
      (customerProfile?.isInternetUpdated && customerProfile?.isElectricityUpdated) ||
      customerProfile?.isGasUpdated ||
      customerProfile?.isMobileUpdated;
    return !!customerProfile.isBundleExplicitlyRequestedByCustomer || isNbnMoversBundleFlow;
  }

  public isShowAddress(): boolean {
    const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
    const isShowAddress: boolean = customerProfile.isShowAddress;
    return !!isShowAddress;
  }

  public setBundleConfig(bundleConfig: BundleConfig): void {
    sessionStorage.setItem(SessionStorageKeys.BundleConfig, JSON.stringify(Array.from(bundleConfig.entries())));
  }

  public getBundleConfig(): BundleConfig {
    return new Map(JSON.parse(sessionStorage.getItem(SessionStorageKeys.BundleConfig)));
  }

  public setBundleConfigProduct(productCategory: ProductCategory, options: BundleConfigOptions): void {
    const bundleConfig = this.getBundleConfig();

    if (bundleConfig.has(productCategory)) {
      const savedOptions = bundleConfig.get(productCategory);
      bundleConfig.set(productCategory, { ...savedOptions, ...options });
    } else {
      bundleConfig.set(productCategory, options);
    }

    this.setBundleConfig(bundleConfig);
  }

  public getBundleConfigProduct(productCategory: ProductCategory): BundleConfigOptions | null {
    const bundleConfig = this.getBundleConfig();

    if (bundleConfig.has(productCategory)) {
      return bundleConfig.get(productCategory);
    }

    return null;
  }

  public isCurrentFlowOriginatingFromBridgePage(): boolean {
    const bridgePageOriginParam = new URLSearchParams(window.location.search).get(QueryStringParamName.BridgePageOrigin);
    return bridgePageOriginParam === 'true';
  }

  public mapBundleTileItems(cartDetails: RetrieveCartResponse): BundleTileItems {
    const bundleTileItems = new BundleTileItems();
    bundleTileItems.energy = new BundleItem();
    bundleTileItems.nbn = new BundleItem();
    bundleTileItems.mobile = new BundleItem();
    bundleTileItems.energy.displayItems = [];
    bundleTileItems.nbn.displayItems = [];
    bundleTileItems.mobile.displayItems = [];
    let nbnCost = 0;
    let mobileCost = 0;
    let hasVoip = false;
    let hasModem = false;
    if (cartDetails) {
      cartDetails.cartItems.map((item: RetrieveCartItem) => {
        switch (item.additionalData?.productCategory?.toLowerCase() as ProductCategory) {
          case 'energy': {
            const prefix = item.additionalData?.productQualification?.fuelType
              ? `${item.additionalData?.productQualification?.fuelType} `
              : '';
            bundleTileItems.energy.displayItems.push({
              productName: `${prefix}${item.additionalData?.productDisplayAttributes?.productName ?? ''}`,
              itemCount: 1
            });
            break;
          }
          case 'nbn': {
            bundleTileItems.nbn.displayItems.push({
              productName: item.additionalData?.productDisplayAttributes?.productName ?? '',
              itemCount: 1,
              paymentFrequency: item.additionalData?.productDisplayAttributes?.paymentFrequency ?? '',
              paymentPlanPrice: item.additionalData?.productDisplayAttributes?.paymentPlanPrice ?? ''
            });
            const nbnPrice = Number((item.additionalData?.productDisplayAttributes as NbnProductDisplayAttributes).price);
            const nonCouponDiscount = CartUtils.getPromotionDiscount(item?.productDiscounts, false);
            nbnCost += nonCouponDiscount ? CartUtils.calculateDiscountedPrice(nbnPrice, nonCouponDiscount?.discountAmount) : nbnPrice;
            break;
          }
          case 'modem': {
            hasModem = true;
            const modem = new BundleItem();
            modem.displayItems = [];
            modem.displayItems.push({ productName: item.additionalData?.productDisplayAttributes?.productName ?? '', itemCount: 1 });
            const nonCouponDiscount = CartUtils.getPromotionDiscount(item?.productDiscounts, false);
            const modemPrice = Number((item.additionalData?.productDisplayAttributes as NbnProductDisplayAttributes).price);
            const modemCost = nonCouponDiscount
              ? CartUtils.calculateDiscountedPrice(modemPrice, nonCouponDiscount?.discountAmount)
              : modemPrice;
            modem.subtotal = `${modemCost}`;
            bundleTileItems.modem = modem;
            break;
          }
          case 'voip':
            hasVoip = true;
            nbnCost += Number((item.additionalData?.productDisplayAttributes as NbnProductDisplayAttributes).price);
            break;
          case 'mobile plan': {
            const existingItem = bundleTileItems.mobile.displayItems?.find((mobileItem) => mobileItem.sku === item.sku);
            if (existingItem) {
              existingItem.itemCount++;
            } else {
              bundleTileItems.mobile.displayItems.push({
                productName: item.additionalData?.productDisplayAttributes?.productName ?? '',
                sku: item.sku,
                itemCount: 1
              });
            }
            const mobilePrice = Number((item.additionalData?.productDisplayAttributes as MobileProductDisplayAttributes).price);
            const nonCouponDiscount = CartUtils.getPromotionDiscount(item?.productDiscounts, false);
            mobileCost += nonCouponDiscount
              ? CartUtils.calculateDiscountedPrice(mobilePrice, nonCouponDiscount?.discountAmount)
              : mobilePrice;
            break;
          }
          default:
            break;
        }
      });
      if (bundleTileItems.nbn.displayItems.length) {
        if (hasVoip && hasModem) {
          bundleTileItems.nbn.displayItems[0].productInfo = '(With home phone & modem)';
        } else if (hasVoip) {
          bundleTileItems.nbn.displayItems[0].productInfo = '(With home phone)';
        } else if (hasModem) {
          bundleTileItems.nbn.displayItems[0].productInfo = '(With modem)';
        }
      }
      bundleTileItems.nbn.subtotal = `$${nbnCost} Per Month`;
      bundleTileItems.mobile.subtotal = `$${mobileCost} Per Month`;
    }
    return bundleTileItems;
  }

  public getBundleDiscountByCategory(category = 'mobile plan', isCouponDiscount = false): Discount[] | null {
    const bundleProducts = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.bundleProducts));
    if (bundleProducts?.discounts?.length) {
      return bundleProducts.discounts
        .filter((discount) => discount.productCategory.applyTo === category)
        .map(({ description, discountAmount }) => {
          return {
            description,
            discountAmount,
            frequency: Frequency.Monthly,
            isCouponDiscount
          };
        });
    }
    return null;
  }

  public mapMobileProductCatalogueAsCartItem(
    productDetail: ProductCatalogueProduct,
    productDiscounts: Discount[] = null
  ): RetrieveCartItem {
    return {
      productDiscounts,
      additionalData: {
        parentCartItemId: null,
        productCategory: 'mobile plan',
        productQualification: {
          customerType: CustomerType.residential
        },
        productDisplayAttributes: {
          productName: productDetail.content?.productName,
          price: productDetail.price,
          dataAllowance: productDetail.attributes.data_allowance,
          contractType: productDetail.attributes.contract_type,
          contractLengthUom: productDetail.attributes.contract_length_uom,
          contractLengthValue: productDetail.attributes.contract_length_value
        }
      },
      cartItemId: productDetail.cartItemId ?? Guid.newGuid(),
      sku: productDetail.sku
    } as unknown as RetrieveCartItem;
  }

  public toggleStaticContent(selectedCategory: 'none' | ProductCategory = 'none'): void {
    const allContentElements = document.querySelectorAll('agl-spa-shell');
    allContentElements.forEach((item) => {
      const dataSpa = item.getAttribute('data-spa');
      const dataSpaParamValue = getDataSpaSearchParams(dataSpa);
      const contentCategory = dataSpaParamValue?.get(DataSpaKey.contentCategory);
      if (contentCategory) {
        const sectionElement = item?.closest('section.container') as HTMLElement;
        if (sectionElement) {
          sectionElement.style.display = contentCategory === selectedCategory ? 'block' : 'none';
        }

        // Toggle separator element
        const previousSibling = sectionElement?.previousElementSibling as HTMLElement;
        if (previousSibling?.classList.contains('separator')) {
          previousSibling.style.display = contentCategory === selectedCategory ? 'block' : 'none';
        }
      }
    });
  }

  public validateActiveCategory(
    firstAvailableCategory: ProductCategory | 'none',
    activeCategory: ProductCategory | 'none'
  ): ProductCategory | 'none' {
    const categoryPriorities = { none: 0, energy: 1, nbn: 2, 'mobile plan': 3 };
    if (
      categoryPriorities[firstAvailableCategory] !== undefined &&
      categoryPriorities[activeCategory] !== undefined &&
      categoryPriorities[firstAvailableCategory] > categoryPriorities[activeCategory]
    ) {
      return firstAvailableCategory;
    }
    return activeCategory;
  }
}

export const bundleService = new BundleService();
